import React, { useEffect, useState } from 'react'
import { Alert } from 'react-bootstrap'
import { useObserver } from 'mobx-react'
import { Subheader } from '../../../components/Page/logix2020/Subheader'
import { t } from 'i18next'
import { FlexWrapper } from '../../../components/Page/logix2020/FlexWrapper'
import { SidebarLeft } from '../../../components/Page/logix2020/SidebarLeft'
import SidebarNavigation from '../../../components/Page/logix2020/SidebarNavigation'
import { useLocation, useParams, useNavigate } from 'react-router-dom'
import { ContentArea } from '../../../components/Page/logix2020/ContentArea'
import ArticleDataProvider, {
  useArticleDataState,
} from '../../../stores/Configuration/ArticleDataProvider'
import ArticleMainForm from '../../../components/Configuration/Articles/ArticleMainForm'
import ArticleOfficeAvailabilities from '../../../components/Configuration/Articles/ArticleOfficeAvailabilities'
import SizeSystemsOverview from '../../../components/Configuration/Articles/SizeSytems/EditSize/SizeSystemsOverview'
import DocumentContainers from '../../../components/Configuration/Articles/Documents/DocumentContainers'
import DispositionThreshold  from '../../../components/Configuration/Articles/DispositionThreshold'
import i18n from '../../../translation/i18n'
import useStores from '../../../stores/useStore'
import { emptyGuid } from '../../../utils/emptyGuid'
import { ButtonWithLoadingState } from '../../../components/Buttons'
import styled from 'styled-components'
//import { ErrorAlerts } from '../../../utils/api/makeCall'
import { getLanguageValue } from '../../../utils/getLanguageValue'
import SubheaderTitle from '../../../components/Page/logix2020/SubheaderTitle'
import useMountEffect from '../../../utils/hooks/useMountEffect'

const StyledButtonsWrapper = styled.div`
  @media screen and (max-width: 767px) {
    display: flex;
    flex-direction: column;
    width: 100%;
    .btn {
      margin: 3px 0;
    }
  }
`
const AlertMessagesWrapper = styled.div`
  max-width: 600px;
`
const navigationLeft = () => [
  {
    key: '#main',
    to: '#main',
    name: i18n.t('articleAdmin:mainData'),
    disabled: false,
  },
  {
    key: '#sizeSystems',
    to: '#sizeSystems',
    name: i18n.t('articleAdmin:availableSizeSystems'),
    disabled: false,
  },
  {
    key: '#activateArticle',
    to: '#activateArticle',
    name: i18n.t('articleAdmin:activateArticleForUnits'),
    disabled: false,
  },
  {
    key: '#documents',
    to: '#documents',
    name: i18n.t('articleAdmin:availableDocuments'),
    disabled: false,
  },
  {
    key: '#contingents',
    to: '#contingents',
    name: i18n.t('articleAdmin:contingentAssignments'),
    disabled: false,
  },
  {
    key: '#catalogs',
    to: '#catalogs',
    name: i18n.t('articleAdmin:catalogAssignments'),
    disabled: false,
  },
  {
    key: '#dispoSettings',
    to: '#dispoSettings',
    name: i18n.t('articleAdmin:dispositionThreshold'),
    disabled: false,
  },
]

const EditArticlePageContent = () => {
  const currentLocation = useLocation()
  const {
    articleBaseObj,
    setArticleBaseObj,
    getArticleBaseData,
    patchArticleBaseData,
    resetArticlesPage,
    successStates,
    setSuccessStates,
    errorStates,
    loadingStates,
    isLoading,
    setIsLoading,
    setActionMode,
    documentContainers,
    setDocumentContainers,
    getDocumentContainers,
    saveArticleOfficeAvailabilities,
    selectedOfficeAvailabilities,
    setSelectedOfficeAvailabilities,
    selectedOfficeAvailabilitiesHasChanged,
    getArticleOfficeAvailabilities,
  } = useArticleDataState()
  const [activeNav, setActiveNav] = useState('')
  const { articleId } = useParams()
  const { officeStore, uiStore } = useStores()
  const allowEntitlementAdministration = useObserver(
    () => uiStore.allowEntitlementAdministration
  )
  const showOOReqGenerator = useObserver(
    () => uiStore.showOOReqGenerator
  )
  const navigate = useNavigate()

  const loadData = async () => {
    getArticleBaseData(articleId)
    getArticleOfficeAvailabilities(articleId)
    getDocumentContainers(articleId)
  }
  useEffect(() => {
    return () => resetArticlesPage()
  }, [resetArticlesPage])

  useMountEffect(() => {
    setActionMode('edit')
    loadData()
  })

  useEffect(() => {
    let timer = null
    if (
      successStates.saveArticle ||
      successStates.saveSizeSystems ||
      successStates.saveArticleOfficeAvailabilities
    ) {
      clearTimeout(timer)
      timer = setTimeout(() => {
        setSuccessStates({})
      }, 3000)
    }
    return () => clearTimeout(timer)
  }, [setSuccessStates, successStates])

  useEffect(() => {
    if (
      currentLocation.hash === '#catalogs' ||
      currentLocation.hash === '#contingents'
    ) {
      sessionStorage.setItem(
        'searchArticleNumber',
        JSON.stringify(articleBaseObj.number)
      )
      switch (currentLocation.hash) {
        case '#contingents':
          navigate(`/contingentTemplatesAdministration`)
          break
        case '#catalogs':
        default:
          navigate(`/catalogsAdministration`)
          break
      }
    }
    setActiveNav(currentLocation.hash || '#main')
  }, [currentLocation.hash, articleBaseObj.number, navigate])

  const selectAllOffices = () => {
    const allOfficeIds = officeStore.loadedOfficeIds.filter(
      (id) => id !== emptyGuid
    )
    setSelectedOfficeAvailabilities(allOfficeIds)
  }

  const deselectAllOffices = () => {
    setSelectedOfficeAvailabilities([])
  }

  const saveOfficeAvailabilities = async () => {
    setIsLoading(true)
    await saveArticleOfficeAvailabilities(
      articleId,
      selectedOfficeAvailabilities
    )
    await getArticleOfficeAvailabilities(articleId)
  }
  const saveBaseData = () => {
    patchArticleBaseData(articleId)
  }
  const renderSizeData = () => {
    return (
      <>
        <SizeSystemsOverview />
        <div className='spacing-down'></div>
        {renderErrors()}
        {renderSuccess()}
      </>
    )
  }
  const renderBaseData = () => {
    return (
      <>
        <ArticleMainForm
          articleBaseObj={articleBaseObj}
          setArticleBaseObj={setArticleBaseObj}
          loadingStates={loadingStates}
          errorStates={errorStates}
        />
        <div className='spacing-down'>
          <ButtonWithLoadingState
            className='mb-2'
            buttonText={t('saveButton')}
            isLoading={loadingStates.saveArticle}
            onClick={saveBaseData}
            disabled={false}
          />
        </div>
        {renderErrors()}
        {renderSuccess()}
      </>
    )
  }
  const renderActivateArticleData = () => {
    return (
      <>
        <ArticleOfficeAvailabilities
          selectedOffices={selectedOfficeAvailabilities}
          setSelectedOffices={setSelectedOfficeAvailabilities}
          loadingStates={loadingStates}
        />
        <AlertMessagesWrapper>
          {renderErrors()}
          {renderSuccess()}
        </AlertMessagesWrapper>
      </>
    )
  }
  const renderDocumentData = () => {
    return (
      <>
        <DocumentContainers
          documentContainers={documentContainers}
          setDocumentContainers={setDocumentContainers}
          loadingStates={loadingStates}
          errorStates={errorStates}
        />
      </>
    )
  }
  const renderDispositionThreshold = () => {
    return (
      <>
        <DispositionThreshold />
      </>
    )
  }
  const renderErrors = () => {
    return errorStates.saveArticle ||
      errorStates.saveSizeSystems ||
      errorStates.saveArticleOfficeAvailabilities ||
      errorStates.deleteSize ? (
      <Alert variant='danger'>
        {
          errorStates.deleteSize
            ? errorStates.deleteSize.value
            : t(
                'articleAdminOverlayProgress:danger'
              ) /*errorStates.saveSizeSystems.value*/
        }
      </Alert>
    ) : null
  }
  const renderSuccess = () => {
    return successStates.saveArticle ||
      successStates.saveSizeSystems ||
      successStates.saveArticleOfficeAvailabilities ? (
      <Alert variant='success' className='mt-2'>
        {t('articleAdmin:successMsg') /*errorStates.saveSizeSystems.value*/}
      </Alert>
    ) : null
  }
  const getOptions = () => {
    let options = navigationLeft()
    if (!(articleBaseObj?.class?.isWithSizes ?? false))
      options = options.filter((item) => item.key !== '#sizeSystems')
    if (!allowEntitlementAdministration)
      options = options.filter((item) => item.key !== '#contingents')
    if (!showOOReqGenerator)
      options = options.filter((item) => item.key !== '#dispoSettings')
    return options
  }

  return (
    <div>
      <Subheader className='subHeader-v1 d-flex justify-content-between'>
        <SubheaderTitle
          withBackButton
          smallTitle={getLanguageValue(articleBaseObj.internalName.values)}
          goBackLabel={t('articleAdmin:goBackArticleOverview')}
          defaultBackTo='/articlesAdministration'
        />
        {activeNav === '#activateArticle' && (
          <StyledButtonsWrapper className='d-flex justify-content-end'>
            <ButtonWithLoadingState
              className='m-2'
              buttonText={t('catalogsAdministration:selectAll')}
              onClick={() => selectAllOffices()}
            />
            <ButtonWithLoadingState
              className='m-2'
              buttonText={t('catalogsAdministration:deSelectAll')}
              onClick={() => deselectAllOffices()}
            />
            <ButtonWithLoadingState
              disabled={!selectedOfficeAvailabilitiesHasChanged}
              className='m-2'
              buttonText={t('saveButton')}
              isLoading={isLoading}
              onClick={() => saveOfficeAvailabilities()}
            />
          </StyledButtonsWrapper>
        )}
        {activeNav === '#main' && (
          <StyledButtonsWrapper className='d-flex justify-content-end'>
            <ButtonWithLoadingState
              className='m-2'
              buttonText={t('saveButton')}
              isLoading={loadingStates.saveArticle}
              onClick={saveBaseData}
              disabled={false}
            />
          </StyledButtonsWrapper>
        )}
      </Subheader>

      <FlexWrapper>
        <SidebarLeft>
          <SidebarNavigation options={getOptions()} active={activeNav} />
        </SidebarLeft>
        <ContentArea>
          {/*<ErrorAlerts errorStates={errorStates} />*/}
          {/* {renderErrors()} */}
          {/* {renderSuccess()} */}
          {activeNav === '#main' && renderBaseData()}
          {activeNav === '#sizeSystems' && renderSizeData()}
          {activeNav === '#activateArticle' && renderActivateArticleData()}
          {activeNav === '#documents' && renderDocumentData()}
          {activeNav === '#dispoSettings' && renderDispositionThreshold()}
        </ContentArea>
      </FlexWrapper>
    </div>
  )
}

const EditArticlePage = () => {
  return useObserver(() => (
    <ArticleDataProvider>
      <EditArticlePageContent />
    </ArticleDataProvider>
  ))
}

export default EditArticlePage
